/**
 * Create form for a subject.
 */
import * as React from 'react';
import { FileText } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { Input } from '@/components/forms/Input';
import { Select } from '@/components/forms/Select';
import { Checkbox } from '@/components/forms/Checkbox';
import { createSubject } from '@/services/subjects.service';
import type { CreateSubjectDto } from '@/types/subject.types';
import { useFormValidation } from '@/hooks/useFormValidation';
import { subjectValidationSchema } from '@/utils/validation-schemas-utils';
import { SlidePanelForm } from '@/components/common/SlidePanelForm';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { SUBJECT_CYCLE_FORM_OPTIONS } from './subject-configs';
import {
  applySubjectFormField,
  emptyCreateSubjectForm,
  resolveEducationCycle,
} from './subject-form-utils';

/** Props for {@link CreateSubjectView}. */
interface CreateSubjectViewProps {
  onClose: () => void;
  onSuccess?: () => void;
}

/** Create form for a subject. */
export const CreateSubjectView: React.FC<CreateSubjectViewProps> = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = React.useState<CreateSubjectDto>(emptyCreateSubjectForm);

  const validation = useFormValidation(subjectValidationSchema);

  const createMutation = useMutation({
    mutationFn: (dto: CreateSubjectDto) => createSubject(dto),
    onSuccess: () => {
      toast.success('Matière créée avec succès !');
      onSuccess?.();
      onClose();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  const isSubmitting = createMutation.isPending;

  /** Patch one field and re-validate. */
  const handleFieldChange = (
    field: keyof CreateSubjectDto,
    value: string | number | boolean | undefined,
  ) => {
    const updatedData = applySubjectFormField(formData, field, value);
    setFormData(updatedData);
    validation.validateField(field, value, updatedData);
    validation.markAsTouched(field);
    validation.markAsDirty(field);
  };

  const handleBlur = (field: string) => {
    validation.markAsTouched(field);
  };

  /** Validate then POST create DTO. */
  const handleSubmit = () => {
    if (!validation.validateForm(formData)) return;
    createMutation.mutate(formData);
  };

  return (
    <SlidePanelForm
      formType="create"
      title="Créer une matière"
      subtitle="Renseignez les informations de la matière"
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Créer"
      isSubmitting={isSubmitting}
      isValid={validation.isFormValid()}
    >
      <div className="form-section">
        <h4 className="form-section-title">
          <FileText className="w-4 h-4" />
          Informations générales
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Code"
            value={formData.code}
            onChange={(e) => handleFieldChange('code', e.target.value)}
            onBlur={() => handleBlur('code')}
            placeholder="Ex: MATH"
            maxLength={50}
            required
            helperText="Code unique identifiant la matière"
            state={validation.getFieldState('code')}
            error={validation.getFieldError('code')}
          />

          <Input
            label="Libellé"
            value={formData.label}
            onChange={(e) => handleFieldChange('label', e.target.value)}
            onBlur={() => handleBlur('label')}
            placeholder="Ex: Mathématiques"
            maxLength={255}
            required
            helperText="Nom complet de la matière"
            state={validation.getFieldState('label')}
            error={validation.getFieldError('label')}
          />
        </div>

        <Input
          label="Description"
          value={formData.description || ''}
          onChange={(e) => handleFieldChange('description', e.target.value)}
          onBlur={() => handleBlur('description')}
          placeholder="Ex: Enseignement des mathématiques"
          maxLength={500}
          state={validation.getFieldState('description')}
          error={validation.getFieldError('description')}
        />

        <Select
          label="Cycle éducatif"
          options={SUBJECT_CYCLE_FORM_OPTIONS}
          value={formData.educationCycle || ''}
          placeholder="Sélectionner un cycle (optionnel)"
          onChange={(value) => handleFieldChange('educationCycle', resolveEducationCycle(value))}
          onBlur={() => handleBlur('educationCycle')}
          state={validation.getFieldState('educationCycle')}
          error={validation.getFieldError('educationCycle')}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="pt-4">
            <Checkbox
              label="Matière principale"
              checked={formData.isCore || false}
              onCheckedChange={(checked) => handleFieldChange('isCore', checked)}
            />
          </div>
        </div>
      </div>
    </SlidePanelForm>
  );
};
